/**
 * 네비게이션 구조 조회 훅 (TanStack Query)
 *
 * - GET /api/nav   사이드바 섹션·항목 순서(데이터 기반)
 *
 * 동적 메타모델: 사이드바 그룹 레이블·순서는 DB 시드(nav_sections)로 정의.
 * 모듈별 하드코딩 섹션 배치 금지 — 서버 응답으로만 결정.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { resolveLabel } from "@/lib/i18n/resolveLabel";
import { queryKeys } from "@/lib/query-keys";

/** 사이드바 항목 (모듈 or 시스템 특수 항목) */
export interface NavEntry {
  id: string;
  sectionId: string | null;
  /** 'module' = 일반 모듈 버킷 / 'system' = 대시보드 등 특수 항목 */
  kind: "module" | "system";
  /** module → modules.code (DB 저장 케이스, 대문자 정규화 필요) / system → 'dashboard' 등 */
  ref: string;
  sort: number;
}

/** 사이드바 섹션 (그룹 레이블 단위) */
export interface NavSectionView {
  id: string;
  label: string;
  /** 레이블 다국어 맵(시스템 기본 섹션만, 사용자가 이름을 바꾸면 빈 맵). 표시는 resolveLabel(label, labelI18n, lang). */
  labelI18n?: Record<string, string>;
  sort: number;
  isDefault: boolean;
  entries: NavEntry[];
}

/**
 * 사이드바 섹션 표시 제목 — 현재 언어의 번역(labelI18n)을 우선, 없으면(사용자가 만들거나 이름을 바꾼 섹션) 저장된 label.
 * 편집 입력은 label(저장된 원문)을 그대로 쓴다.
 */
export function navSectionTitle(
  section: { label: string; labelI18n?: Record<string, string> },
  lang: string,
): string {
  return resolveLabel(section.label, section.labelI18n, lang);
}

/** GET /api/nav 응답 */
export interface NavResponse {
  sections: NavSectionView[];
}

/**
 * 워크스페이스 사이드바 네비게이션 구조.
 * @param slug 현재 워크스페이스 slug
 */
export function useNav(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.nav.all(slug ?? ""),
    queryFn: () =>
      apiFetch<NavResponse>("/api/nav", {
        workspaceSlug: slug,
      }),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
